import { useState, useEffect } from 'react'
import { Save, Building, FileText, Percent, User, Bell, Palette, Shield, Printer, TestTube, Monitor, Sidebar as SidebarIcon, LayoutDashboard } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { LoadingButton } from '../components/ui/LoadingButton'
import { useAuthStore } from '../stores/authStore'
import { useNavigationStore, NavigationStyle } from '../stores/navigationStore'
import { apiClient } from '../services/api'
import printerService from '../services/printerService'
import toast from 'react-hot-toast'

interface BusinessSettings {
  business_name: string
  address: string
  phone: string
  email: string
  gst_number: string
  state: string
  state_code: string
  logo_url?: string
  website?: string
}

interface GST_Settings {
  default_gst_rate: number
  cgst_account: string
  sgst_account: string
  igst_account: string
  enable_hsn_codes: boolean
  enable_reverse_charge: boolean
}

interface PrintSettings {
  invoice_format: 'a4' | 'thermal' | 'a5'
  show_logo: boolean
  show_terms: boolean
  auto_print: boolean
  copies_count: number
  items_per_page: number
}

interface NotificationSettings {
  low_stock_alert: boolean
  low_stock_threshold: number
  daily_sales_report: boolean
  backup_reminder: boolean
  email_notifications: boolean
}

export const Settings = () => {
  const [activeTab, setActiveTab] = useState('business')
  const [loading, setLoading] = useState(false)
  const [testPrinting, setTestPrinting] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const { user } = useAuthStore()
  const { navigationStyle, setNavigationStyle } = useNavigationStore()

  const [businessSettings, setBusinessSettings] = useState<BusinessSettings>({
    business_name: '',
    address: '',
    phone: '',
    email: '',
    gst_number: '',
    state: '',
    state_code: '',
    logo_url: '',
    website: ''
  })

  const [gstSettings, setGstSettings] = useState<GST_Settings>({
    default_gst_rate: 18,
    cgst_account: 'CGST Output',
    sgst_account: 'SGST Output',
    igst_account: 'IGST Output',
    enable_hsn_codes: true,
    enable_reverse_charge: false
  })

  const [printSettings, setPrintSettings] = useState<PrintSettings>({
    invoice_format: 'a4',
    show_logo: true,
    show_terms: true,
    auto_print: false,
    copies_count: 1,
    items_per_page: 15
  })

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    low_stock_alert: true,
    low_stock_threshold: 10,
    daily_sales_report: true,
    backup_reminder: true,
    email_notifications: false
  })

  const handleSave = async (settingsType: string) => {
    setLoading(true)
    setValidationErrors({}) // Clear previous errors
    try {
      let settingsData: any = {}

      switch (settingsType) {
        case 'Business':
          // Client-side validation
          const errors: Record<string, string> = {}

          if (!businessSettings.business_name?.trim()) {
            errors.business_name = 'Business name is required'
          }
          if (!businessSettings.phone?.trim()) {
            errors.phone = 'Phone number is required'
          }
          if (!businessSettings.email?.trim()) {
            errors.email = 'Email is required'
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(businessSettings.email)) {
            errors.email = 'Please enter a valid email address'
          }
          if (!businessSettings.gst_number?.trim()) {
            errors.gst_number = 'GST number is required'
          }
          if (!businessSettings.state?.trim()) {
            errors.state = 'State is required'
          }
          if (!businessSettings.address?.trim()) {
            errors.address = 'Address is required'
          }

          if (Object.keys(errors).length > 0) {
            setValidationErrors(errors)
            toast.error('Please fix the validation errors')
            return
          }

          // Update merchant information instead of settings
          settingsData = {
            business_name: businessSettings.business_name,
            address: businessSettings.address,
            phone: businessSettings.phone,
            email: businessSettings.email,
            gst_number: businessSettings.gst_number,
            state: businessSettings.state,
            website: businessSettings.website
          }

          await apiClient.updateMerchant(settingsData)

          // Update printer service business info
          printerService.setBusinessInfo({
            name: businessSettings.business_name,
            address: businessSettings.address,
            phone: businessSettings.phone,
            email: businessSettings.email,
            gstNumber: businessSettings.gst_number,
            website: businessSettings.website || undefined,
            state: businessSettings.state || undefined
          })

          toast.success('Business settings saved successfully!')
          return // Return early for business settings
        break
        case 'GST':
          settingsData = gstSettings
          break
        case 'Print':
          settingsData = printSettings
          // Update printer service settings
          printerService.setSettings({
            format: printSettings.invoice_format,
            showLogo: printSettings.show_logo,
            showTerms: printSettings.show_terms,
            autoPrint: printSettings.auto_print,
            copiesCount: printSettings.copies_count,
            itemsPerPage: printSettings.items_per_page
          })
          break
        case 'Notification':
          settingsData = notificationSettings
          break
      }
      
      // Save settings to API
      await apiClient.saveSettings(settingsType.toLowerCase(), settingsData)
      toast.success(`${settingsType} settings saved successfully!`)
    } catch (error: any) {
      console.error('Settings save error:', error)

      // Handle validation errors from backend
      if (error?.errors && typeof error.errors === 'object') {
        setValidationErrors(error.errors)
        const errorMessages = Object.values(error.errors).join(', ')
        toast.error(`Validation failed: ${errorMessages}`)
      } else if (error?.message) {
        toast.error(error.message)
      } else {
        toast.error('Failed to save settings')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleTestPrint = async () => {
    setTestPrinting(true)
    try {
      await printerService.testPrint()
      toast.success('Test print sent successfully!')
    } catch (error: any) {
      toast.error(error.message || 'Failed to print test page')
    } finally {
      setTestPrinting(false)
    }
  }

  useEffect(() => {
    // Load settings and business data when component mounts
    const loadSettings = async () => {
      try {
        setLoading(true)
        
        // Load settings from settings table
        const settingsResponse = await apiClient.getSettings()
        
        // Load user and merchant data from current user
        const userResponse = await apiClient.getCurrentUser()
        
        // Update business settings with merchant data
        if (userResponse.user) {
          const userData = userResponse.user
          setBusinessSettings({
            business_name: userData.business_name || '',
            address: userData.address || '',
            phone: userData.business_phone || userData.phone || '',
            email: userData.business_email || userData.email || '',
            gst_number: userData.gst_number || '',
            state: userData.state || '',
            state_code: userData.state_code || '',
            logo_url: userData.logo_url || '',
            website: userData.website || ''
          })
        }
        
        // Update other settings
        if (settingsResponse.gst) {
          setGstSettings({ ...gstSettings, ...settingsResponse.gst })
        }
        if (settingsResponse.print) {
          const loadedPrintSettings = { ...printSettings, ...settingsResponse.print }
          setPrintSettings(loadedPrintSettings)

          // Update printer service with loaded settings
          printerService.setSettings({
            format: loadedPrintSettings.invoice_format || 'thermal',
            showLogo: loadedPrintSettings.show_logo ?? true,
            showTerms: loadedPrintSettings.show_terms ?? true,
            autoPrint: loadedPrintSettings.auto_print ?? false,
            copiesCount: loadedPrintSettings.copies_count || 1,
            itemsPerPage: loadedPrintSettings.items_per_page || 15
          })
        }
        if (settingsResponse.notifications) {
          setNotificationSettings({ ...notificationSettings, ...settingsResponse.notifications })
        }

        // Update business info in printer service
        if (settingsResponse.business) {
          printerService.setBusinessInfo({
            name: settingsResponse.business.business_name || '',
            address: settingsResponse.business.address || '',
            phone: settingsResponse.business.phone || '',
            email: settingsResponse.business.email || '',
            gstNumber: settingsResponse.business.gst_number || '',
            website: settingsResponse.business.website || undefined,
            state: settingsResponse.business.state || undefined
          })
        }

      } catch (error) {
        console.error('Failed to load settings:', error)
        toast.error('Failed to load settings')
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      loadSettings()
    }
  }, [user])

  const tabs = [
    { id: 'business', label: 'Business Info', icon: Building },
    { id: 'gst', label: 'GST Settings', icon: FileText },
    { id: 'print', label: 'Print & Invoice', icon: Printer },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'display', label: 'Display', icon: Monitor },
    { id: 'security', label: 'Security', icon: Shield },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-600">Configure your POS system settings and preferences</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Business Settings */}
      {activeTab === 'business' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Business Information</h2>
            <LoadingButton
              onClick={() => handleSave('Business')}
              isLoading={loading}
              loadingText="Saving..."
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </LoadingButton>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
              <Input
                value={businessSettings.business_name}
                onChange={(e) => {
                  setBusinessSettings(prev => ({ ...prev, business_name: e.target.value }))
                  if (validationErrors.business_name) {
                    setValidationErrors(prev => ({ ...prev, business_name: '' }))
                  }
                }}
                placeholder="Your Business Name"
                className={validationErrors.business_name ? 'border-red-500' : ''}
              />
              {validationErrors.business_name && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.business_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <Input
                value={businessSettings.phone}
                onChange={(e) => {
                  setBusinessSettings(prev => ({ ...prev, phone: e.target.value }))
                  if (validationErrors.phone) {
                    setValidationErrors(prev => ({ ...prev, phone: '' }))
                  }
                }}
                placeholder="+91 9876543210"
                className={validationErrors.phone ? 'border-red-500' : ''}
              />
              {validationErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <Input
                type="email"
                value={businessSettings.email}
                onChange={(e) => {
                  setBusinessSettings(prev => ({ ...prev, email: e.target.value }))
                  if (validationErrors.email) {
                    setValidationErrors(prev => ({ ...prev, email: '' }))
                  }
                }}
                placeholder="business@example.com"
                className={validationErrors.email ? 'border-red-500' : ''}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">GST Number *</label>
              <Input
                value={businessSettings.gst_number}
                onChange={(e) => {
                  setBusinessSettings(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))
                  if (validationErrors.gst_number) {
                    setValidationErrors(prev => ({ ...prev, gst_number: '' }))
                  }
                }}
                placeholder="27AAAAA0000A1Z5"
                className={validationErrors.gst_number ? 'border-red-500' : ''}
              />
              {validationErrors.gst_number && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.gst_number}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
              <Input
                value={businessSettings.state}
                onChange={(e) => {
                  setBusinessSettings(prev => ({ ...prev, state: e.target.value }))
                  if (validationErrors.state) {
                    setValidationErrors(prev => ({ ...prev, state: '' }))
                  }
                }}
                placeholder="Maharashtra"
                className={validationErrors.state ? 'border-red-500' : ''}
              />
              {validationErrors.state && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.state}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State Code</label>
              <Input
                value={businessSettings.state_code}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, state_code: e.target.value }))}
                placeholder="27"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Address *</label>
              <textarea
                value={businessSettings.address}
                onChange={(e) => {
                  setBusinessSettings(prev => ({ ...prev, address: e.target.value }))
                  if (validationErrors.address) {
                    setValidationErrors(prev => ({ ...prev, address: '' }))
                  }
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 ${
                  validationErrors.address ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full business address"
              />
              {validationErrors.address && (
                <p className="text-red-500 text-sm mt-1">{validationErrors.address}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
              <Input
                value={businessSettings.website || ''}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, website: e.target.value }))}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
              <Input
                value={businessSettings.logo_url || ''}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, logo_url: e.target.value }))}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>
        </Card>
      )}

      {/* GST Settings */}
      {activeTab === 'gst' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-800">GST Configuration</h2>
            <LoadingButton
              onClick={() => handleSave('GST')}
              isLoading={loading}
              loadingText="Saving..."
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </LoadingButton>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default GST Rate (%)</label>
              <Input
                type="number"
                min="0"
                max="28"
                step="0.1"
                value={gstSettings.default_gst_rate}
                onChange={(e) => setGstSettings(prev => ({ ...prev, default_gst_rate: parseFloat(e.target.value) }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CGST Account</label>
              <Input
                value={gstSettings.cgst_account}
                onChange={(e) => setGstSettings(prev => ({ ...prev, cgst_account: e.target.value }))}
                placeholder="CGST Output Account"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SGST Account</label>
              <Input
                value={gstSettings.sgst_account}
                onChange={(e) => setGstSettings(prev => ({ ...prev, sgst_account: e.target.value }))}
                placeholder="SGST Output Account"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">IGST Account</label>
              <Input
                value={gstSettings.igst_account}
                onChange={(e) => setGstSettings(prev => ({ ...prev, igst_account: e.target.value }))}
                placeholder="IGST Output Account"
              />
            </div>

            <div className="md:col-span-2">
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={gstSettings.enable_hsn_codes}
                    onChange={(e) => setGstSettings(prev => ({ ...prev, enable_hsn_codes: e.target.checked }))}
                    className="mr-3"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Enable HSN/SAC Codes</span>
                    <p className="text-xs text-gray-500">Show HSN/SAC codes on invoices for tax compliance</p>
                  </div>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={gstSettings.enable_reverse_charge}
                    onChange={(e) => setGstSettings(prev => ({ ...prev, enable_reverse_charge: e.target.checked }))}
                    className="mr-3"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Enable Reverse Charge</span>
                    <p className="text-xs text-gray-500">Support for reverse charge mechanism in GST</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">GST Rate Reference</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-blue-800">
              <div>
                <div className="font-medium">0% GST</div>
                <div className="text-xs">Essential items</div>
              </div>
              <div>
                <div className="font-medium">5% GST</div>
                <div className="text-xs">Basic necessities</div>
              </div>
              <div>
                <div className="font-medium">12% GST</div>
                <div className="text-xs">Processed foods</div>
              </div>
              <div>
                <div className="font-medium">18% GST</div>
                <div className="text-xs">Most goods & services</div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Print Settings */}
      {activeTab === 'print' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Print & Invoice Settings</h2>
            <div className="flex gap-2">
              <LoadingButton
                onClick={handleTestPrint}
                isLoading={testPrinting}
                loadingText="Testing..."
                variant="outline"
              >
                <TestTube className="h-4 w-4 mr-2" />
                Test Print
              </LoadingButton>
              <LoadingButton
                onClick={() => handleSave('Print')}
                isLoading={loading}
                loadingText="Saving..."
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </LoadingButton>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Format</label>
              <select
                value={printSettings.invoice_format}
                onChange={(e) => setPrintSettings(prev => ({ ...prev, invoice_format: e.target.value as 'a4' | 'thermal' | 'a5' }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="a4">A4 Paper</option>
                <option value="a5">A5 Paper</option>
                <option value="thermal">Thermal Receipt</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Copies</label>
              <Input
                type="number"
                min="1"
                max="5"
                value={printSettings.copies_count}
                onChange={(e) => setPrintSettings(prev => ({ ...prev, copies_count: parseInt(e.target.value) }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Items Per Page (A5)</label>
              <Input
                type="number"
                min="5"
                max="25"
                value={printSettings.items_per_page}
                onChange={(e) => setPrintSettings(prev => ({ ...prev, items_per_page: parseInt(e.target.value) }))}
              />
              <p className="text-xs text-gray-500 mt-1">Number of line items per page for A5 invoices (5-25)</p>
            </div>

            <div className="md:col-span-2">
              <div className="space-y-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={printSettings.show_logo}
                    onChange={(e) => setPrintSettings(prev => ({ ...prev, show_logo: e.target.checked }))}
                    className="mr-3"
                  />
                  <span className="text-sm font-medium text-gray-700">Show business logo on invoices</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={printSettings.show_terms}
                    onChange={(e) => setPrintSettings(prev => ({ ...prev, show_terms: e.target.checked }))}
                    className="mr-3"
                  />
                  <span className="text-sm font-medium text-gray-700">Include terms and conditions</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={printSettings.auto_print}
                    onChange={(e) => setPrintSettings(prev => ({ ...prev, auto_print: e.target.checked }))}
                    className="mr-3"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Auto-print invoices after sale</span>
                    <p className="text-xs text-gray-500">Automatically send invoice to printer when sale is completed</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Notification Settings */}
      {activeTab === 'notifications' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Notification Preferences</h2>
            <LoadingButton
              onClick={() => handleSave('Notification')}
              isLoading={loading}
              loadingText="Saving..."
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </LoadingButton>
          </div>

          <div className="space-y-6">
            <div>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={notificationSettings.low_stock_alert}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, low_stock_alert: e.target.checked }))}
                  className="mr-3"
                />
                <span className="text-sm font-medium text-gray-700">Low stock alerts</span>
              </label>
              
              {notificationSettings.low_stock_alert && (
                <div className="ml-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Alert threshold (quantity)</label>
                  <Input
                    type="number"
                    min="1"
                    value={notificationSettings.low_stock_threshold}
                    onChange={(e) => setNotificationSettings(prev => ({ ...prev, low_stock_threshold: parseInt(e.target.value) }))}
                    className="max-w-xs"
                  />
                </div>
              )}
            </div>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.daily_sales_report}
                onChange={(e) => setNotificationSettings(prev => ({ ...prev, daily_sales_report: e.target.checked }))}
                className="mr-3"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Daily sales report</span>
                <p className="text-xs text-gray-500">Receive daily summary of sales and revenue</p>
              </div>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.backup_reminder}
                onChange={(e) => setNotificationSettings(prev => ({ ...prev, backup_reminder: e.target.checked }))}
                className="mr-3"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Backup reminders</span>
                <p className="text-xs text-gray-500">Weekly reminders to backup your data</p>
              </div>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notificationSettings.email_notifications}
                onChange={(e) => setNotificationSettings(prev => ({ ...prev, email_notifications: e.target.checked }))}
                className="mr-3"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Email notifications</span>
                <p className="text-xs text-gray-500">Send notifications to your email address</p>
              </div>
            </label>
          </div>
        </Card>
      )}

      {/* Display Settings */}
      {activeTab === 'display' && (
        <Card className="p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Display Settings</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Customize the appearance and navigation style of your POS system</p>
          </div>

          <div className="space-y-6">
            {/* Navigation Style */}
            <div>
              <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Navigation Style</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Choose how you want to navigate through the application</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sidebar Navigation Option */}
                <button
                  onClick={() => setNavigationStyle('sidebar')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    navigationStyle === 'sidebar'
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${navigationStyle === 'sidebar' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      <SidebarIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-medium ${navigationStyle === 'sidebar' ? 'text-orange-700 dark:text-orange-300' : 'text-gray-900 dark:text-gray-100'}`}>
                          Sidebar Navigation
                        </h4>
                        {navigationStyle === 'sidebar' && (
                          <Badge variant="success" className="text-xs">Active</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Classic vertical navigation on the left side. Best for desktop users who prefer traditional layout.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Horizontal Navigation Option */}
                <button
                  onClick={() => setNavigationStyle('horizontal')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    navigationStyle === 'horizontal'
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${navigationStyle === 'horizontal' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      <LayoutDashboard className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-medium ${navigationStyle === 'horizontal' ? 'text-orange-700 dark:text-orange-300' : 'text-gray-900 dark:text-gray-100'}`}>
                          Horizontal Navigation
                        </h4>
                        {navigationStyle === 'horizontal' && (
                          <Badge variant="success" className="text-xs">Active</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Modern horizontal navigation at the top. Provides more screen space for content.
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Monitor className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-blue-900 dark:text-blue-300">Navigation Preview</h4>
                    <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                      Your navigation style will change immediately after selecting an option. You can switch back anytime from this settings page.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Security Settings */}
      {activeTab === 'security' && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">Security & Data</h2>
          
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="font-medium text-blue-900">Account Security</h3>
                  <p className="text-sm text-blue-700">Manage your password and account security in your <strong>Profile</strong> page</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-medium text-gray-900 mb-4">Data & Privacy</h3>
              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-start" onClick={() => toast.info('Data export feature coming soon!')}>
                  Export All Data
                </Button>
                <Button variant="outline" className="w-full justify-start text-red-600 hover:text-red-700 hover:border-red-300" onClick={() => toast.error('This action cannot be undone!')}>
                  Delete All Data (Irreversible)
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}